import React, { useEffect } from 'react';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { DropdownMenu } from '@uhg-abyss/web/ui/DropdownMenu';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { saveAs } from 'file-saver';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useSelector } from 'react-redux';
import { LagoonContext } from '@src/AppContext';
import { LeftNav } from './LeftNav';
import { PageContent } from './PageContent';
import { OnThisPage } from './OnThisPage/OnThisPage';
import { leftNavMap, leftNavData } from '@src/common/Utils/utils';
import { animateScroll } from 'react-scroll';
import { config } from '@uhg-abyss/web/tools/config';
import { useUserInfo } from '@src/hooks/useUserInfo';
import { useDSUserInfo } from '@src/hooks/useDsUserInfo';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';

const BorderLine = styled('div', {
  marginBottom: '20px',

  '@screen >= $lg': {
    borderBottomColor: '#5a5a5a',
    borderBottom: '1px solid',
  },
});

const ResponsivePageMargin = styled('div', {
  marginRight: '24px',
  marginLeft: '24px',

  '@screen >= $xl': {
    marginRight: '0px',
    marginLeft: '0px',
  },
});

const FlexStyle = styled('div', {
  display: 'flex',
  flexDirection: 'row',
});

const ResponsiveDivItemLeftNav = styled('div', {
  flex: '0 0 0',

  '@screen >= $lg': {
    flex: '0 0 20%',
  },
});

const LeftNavResponsive = styled('div', {
  '@screen < $lg': {
    display: 'none',
  },
});

const ResponsiveDivContent = styled('div', {
  flex: '0 0 80%',
  paddingTop: '24px',

  '@screen < $lg': {
    flex: '0 0 100%',
  },
});

const PageHeading = styled('h1', {
  color: '#002677',
  fontStyle: 'normal',
  fontWeight: '600 !important',
  fontSize: '36.48px !important',
  lineHeight: '48px',
  margin: 0,
  fontFamily: 'UHC Serif Headline',
});

const UpdatedDate = styled('p', {
  color: 'rgb(0, 38, 119)',
  fontSize: '13px',
  fontWeight: '400',
  minHeight: '20px',
  lineHeight: '20px',
  marginTop: '8px',
  display: 'flex',
});

const IconPadding = styled('div', {
  paddingRight: '10px',
});

const ResponsiveDiv = styled('div', {
  display: 'flex',

  '@screen >= $lg': {
    marginRight: '0px',
  },
});

const HeaderResponsiveDiv = styled('div', {
  display: 'block',

  '@screen >= $lg': {
    display: 'flex',
    marginRight: '0px',
  },
});

const ResponsiveDivItemLeft = styled('div', {
  flex: '0 0 100%',

  '@screen >= $lg': {
    flex: '0 0 80%',
  },
});

const ResponsiveDivItemLeftFullWidth = styled('div', {
  flex: '0 0 100%',
});

const ResponsiveDivItemRight = styled('div', {
  flex: '0 0 20%',

  '@screen < $lg': {
    flex: '0 0 0',
  },
});

const OnThisPageDisplay = styled('div', {
  '@screen < $lg': {
    display: 'none',
  },
});

export const KnowledgeBase = () => {
  const { navigate } = useRouter();
  const { resource } = useParams();
  const [, getUserInfo] = useUserInfo();
  const [, getDSUserInfo] = useDSUserInfo();

  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const userDSInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const isUserSignedIn = userInfo && userInfo.uuid;
  // Check if resource contains 'faqs'
  const isFaqsResource = resource && resource.toLowerCase().includes('faqs');

  const isShowLoader =
    resource &&
    resource.toLowerCase().includes('video-demos') &&
    !userInfo?.firstName;
  useEffect(() => {
    const isdemoVideosResource =
      resource && resource.toLowerCase().includes('video-demos');
    if (isdemoVideosResource) {
      if (!document.getElementById('auth-script')) {
        const script = document.createElement('script');

        script.src = config('UHC_AUTH_SCRIPT');
        script.async = false;
        script.id = 'auth-script';

        document.head.appendChild(script);
      }

      if (!userInfo.uuid) {
        getUserInfo();
      }
      if (userDSInfo?.uuid === '') {
        getDSUserInfo({ userInfo });
      }
    }
  }, []);

  const lagoonContext = React.useContext(LagoonContext);
  const leftNavHeaders = lagoonContext
    ?.getTable('knowledge-base-left-nav-headers')
    ?.getRow();
  const leftNavHeaderItems = lagoonContext
    ?.getTable('knowledge-base-header-items-v2')
    ?.getRow();
  const content = lagoonContext?.getTable('knowledge-base-content').getRow();
  const dataDictionaries = lagoonContext
    ?.getTable('data-dictionaries')
    .getRow();

  let leftNavContent = leftNavHeaders;
  const leftHeaderItems = leftNavHeaderItems;

  leftNavContent = leftNavMap(leftNavContent, leftHeaderItems);

  let knowledgeBaseData = {
    pathsByTag: leftNavContent,
  };

  const getLeftNavData = () => {
    const dataList = leftNavData(userInfo, leftNavContent, leftHeaderItems);
    knowledgeBaseData = {
      pathsByTag: dataList,
    };

    return knowledgeBaseData;
  };

  const getResource = () => {
    if (resource) {
      return resource;
    }
    return 'api-overview';
  };

  useEffect(() => {
    animateScroll.scrollToTop();
  }, [resource]);

  useEffect(() => {
    if (!resource) {
      navigate('/knowledgebase/api-overview');
    }
  }, []);

  useEffect(() => {
    // Dynatrace page tagging + SPA route-change action
    const dtrum = window?.dtrum;
    if (!dtrum) {
      return;
    }

    const currentResource = getResource();
    const pageName = `apimarketplace:knowledge-base:${currentResource}`;

    dtrum.setPage({
      name: pageName,
      group: `apimarketplace-${config('APP_ENV')}`,
    });
  }, [resource]);

  const pageContentList = content;
  const filteredPageContent = pageContentList?.filter((el) => {
    return el.id === getResource();
  });

  const pageContent = filteredPageContent?.length ? filteredPageContent[0] : '';

  const datadownload = (excelFile, fileName) => {
    saveAs(excelFile, fileName);
  };

  const getDataDictionaryList = () => {
    const dataDictionariesLagoonList = dataDictionaries;

    const resourceDataDictionaryList = dataDictionariesLagoonList?.filter(
      (el) => {
        return resource.includes(el.api.toLowerCase()) && el.active;
      }
    );

    const dataDictionaryList = [];
    if (resourceDataDictionaryList) {
      resourceDataDictionaryList.forEach((element) => {
        const dataObject = {
          title: element.title,
          onClick: () => {
            return datadownload(element.fileUrl, element.fileName);
          },
          icon: <IconSymbol icon="download" size="16px" />,
          isSeparated: element.isSeparated,
        };
        dataDictionaryList.push(dataObject);
      });
    }

    return dataDictionaryList;
  };

  return (
    <ResponsivePageMargin>
      {isShowLoader ? (
        <LoadingOverlay
          loadingTitle="Signing In"
          ariaLoadingLabel="Signing In"
          isLoading
        />
      ) : (
        <FlexStyle>
          <ResponsiveDivItemLeftNav>
            <LeftNavResponsive>
              <LeftNav>
                <LeftNav.Link data={getLeftNavData()} />
              </LeftNav>
            </LeftNavResponsive>
          </ResponsiveDivItemLeftNav>
          <ResponsiveDivContent>
            <HeaderResponsiveDiv>
              <ResponsiveDivItemLeft>
                <PageHeading>{pageContent?.pageHeading}</PageHeading>
                <UpdatedDate>
                  <IconPadding>
                    <IconSymbol icon="update" size="20px" color="$primary1" />
                  </IconPadding>
                  Last Updated {pageContent?.date}
                </UpdatedDate>
                <PageContent
                  content={pageContent?.pageDescription}
                  pageHeading={pageContent?.pageHeading}
                  displayDropDown={false}
                />
              </ResponsiveDivItemLeft>
              <ResponsiveDivItemRight>
                {isUserSignedIn && pageContent?.displayDownloadDropdown && (
                  <React.Fragment>
                    <DropdownMenu
                      css={{
                        'abyss-dropdown-menu-trigger-root': {
                          borderRadius: '4px !important',
                          fontSize: '14px !important',
                        },
                        'abyss-dropdown-menu-label': {
                          fontSize: '14px !important',
                        },

                        '.abyss-action-menu-item-title': {
                          fontSize: '14px !important',
                          color: '#323334 !important',
                          fontWeight: '500 !important',
                        },
                      }}
                      label={pageContent?.downloadDropdownLabel}
                      outline
                      before={
                        <IconSymbol
                          icon="download"
                          size="20px"
                          color="#002677"
                        />
                      }
                      menuItems={getDataDictionaryList()}
                    />
                  </React.Fragment>
                )}
              </ResponsiveDivItemRight>
            </HeaderResponsiveDiv>
            <BorderLine />
            <ResponsiveDiv>
              {isFaqsResource ? (
                // If FAQ resource, show left block at full width and remove right block
                <ResponsiveDivItemLeftFullWidth>
                  <PageContent
                    content={pageContent?.pageContent}
                    displayDropDown
                    isFaqsResource={true}
                  />
                </ResponsiveDivItemLeftFullWidth>
              ) : (
                // Else, show left and right blocks as usual
                <React.Fragment>
                  <ResponsiveDivItemLeft>
                    <PageContent
                      content={pageContent?.pageContent}
                      displayDropDown
                      isFaqsResource={false}
                    />
                  </ResponsiveDivItemLeft>
                  <ResponsiveDivItemRight>
                    <OnThisPageDisplay>
                      <OnThisPage />
                    </OnThisPageDisplay>
                  </ResponsiveDivItemRight>
                </React.Fragment>
              )}
            </ResponsiveDiv>
          </ResponsiveDivContent>
        </FlexStyle>
      )}
    </ResponsivePageMargin>
  );
};
